/* ================================================================
   <side-drawer> — shared open/close controller.

   Public API on each element:
     drawer.open()   — reveal
     drawer.close()  — hide

   Delegated triggers (wired once per page):
     <button data-side-drawer-open="ID">        — opens drawer#ID
     <[...] data-side-drawer-close>              — closes its ancestor drawer

   Also closes on:
     · ESC key
     · scrim click (if the scrim carries data-side-drawer-close)
   ================================================================ */

if (!customElements.get('side-drawer')) {
  class SideDrawer extends HTMLElement {
    constructor() {
      super();
      this._onKey = this._onKey.bind(this);
      this._onClick = this._onClick.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this._onClick);

      // Teleport to <body> so we escape any nested stacking context
      // (buy-box, sections with transforms, sticky headers, etc.).
      if (this.parentElement !== document.body) {
        document.body.appendChild(this);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._onKey);
    }

    open() {
      if (!this.hidden) return;
      this.hidden = false;
      this._previouslyFocused = document.activeElement;

      // Double rAF so the transition fires from the hidden -> visible state.
      requestAnimationFrame(() =>
        requestAnimationFrame(() => this.classList.add('is-open'))
      );

      document.body.classList.add('side-drawer-open');
      document.addEventListener('keydown', this._onKey);

      const focusTarget = this.querySelector(
        '[data-side-drawer-autofocus], input, select, textarea, button:not(.side-drawer__close), [href], [tabindex]:not([tabindex="-1"])'
      ) || this.querySelector('.side-drawer__close');
      if (focusTarget) focusTarget.focus({ preventScroll: true });

      this.dispatchEvent(new CustomEvent('side-drawer:open', { bubbles: true }));
    }

    close() {
      if (this.hidden) return;
      this.classList.remove('is-open');

      const panel = this.querySelector('.side-drawer__panel');
      const finish = () => {
        this.hidden = true;
        document.body.classList.remove('side-drawer-open');
        if (this._previouslyFocused && this._previouslyFocused.focus) {
          this._previouslyFocused.focus({ preventScroll: true });
        }
        this.dispatchEvent(new CustomEvent('side-drawer:close', { bubbles: true }));
      };

      if (panel) {
        let done = false;
        const onEnd = () => { if (done) return; done = true; finish(); panel.removeEventListener('transitionend', onEnd); };
        panel.addEventListener('transitionend', onEnd, { once: true });
        setTimeout(onEnd, 500); // fallback if transitionend doesn't fire
      } else {
        finish();
      }

      document.removeEventListener('keydown', this._onKey);
    }

    _onKey(e) {
      if (e.key === 'Escape') this.close();
    }

    _onClick(e) {
      if (e.target.closest('[data-side-drawer-close]')) {
        e.preventDefault();
        this.close();
      }
    }
  }

  customElements.define('side-drawer', SideDrawer);

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-side-drawer-open]');
    if (!trigger) return;
    const id = trigger.getAttribute('data-side-drawer-open');
    const drawer = id && document.getElementById(id);
    if (drawer && typeof drawer.open === 'function') {
      e.preventDefault();
      drawer.open();
    }
  });
}
